import { useEffect, useState } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMoneyBill,
  faChartPie,
  faChartLine,
} from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import { useAuth } from "../../../Auths/CheckUser";

const SewaPayment = () => {
  const { user } = useAuth();

  const [totalCollection, setTotalCollection] = useState(0);
  const [activeTotal, setActiveTotal] = useState(0);
  const [nonActiveTotal, setNonActiveTotal] = useState(0);

  const [weeklyActive, setWeeklyActive] = useState(0);
  const [weeklyNonActive, setWeeklyNonActive] = useState(0);
  const [monthlyActive, setMonthlyActive] = useState(0);
  const [monthlyNonActive, setMonthlyNonActive] = useState(0);

  useEffect(() => {
    if (!user || !user.id) return;

    const fetchStaffClients = async () => {
      try {
        const res = await axios.get(`https://bpohasil.ngrok.app/api/user/${user.id}`);
        const staffData = res.data;
        const clientList = Array.isArray(staffData.clients) ? staffData.clients : [];

        // Calculate totals directly
        calculateTotals(clientList);
      } catch (err) {
        console.error("Error fetching staff clients:", err);
      }
    };

    fetchStaffClients();
  }, [user]);

  const calculateTotals = (records) => {
    let total = 0;
    let active = 0;
    let nonActive = 0;

    const now = new Date();
    const weekAgo = new Date();
    weekAgo.setDate(now.getDate() - 7);

    let wActive = 0;
    let wNonActive = 0;
    let mActive = 0;
    let mNonActive = 0;

    records.forEach((item) => {
      const payment = item.payment || 0;
      const date = new Date(item.createdAt);
      total += payment;

      const isActive = item.status === "AKTIF" || item.status === "Active";

      // Overall totals
      if (isActive) active += payment;
      else nonActive += payment;

      // Weekly
      if (!isNaN(date) && date >= weekAgo && date <= now) {
        if (isActive) wActive += payment;
        else wNonActive += payment;
      }

      // Monthly
      if (
        !isNaN(date) &&
        date.getMonth() === now.getMonth() &&
        date.getFullYear() === now.getFullYear()
      ) {
        if (isActive) mActive += payment;
        else mNonActive += payment;
      }
    });

    setTotalCollection(total);
    setActiveTotal(active);
    setNonActiveTotal(nonActive);

    setWeeklyActive(wActive);
    setWeeklyNonActive(wNonActive);
    setMonthlyActive(mActive);
    setMonthlyNonActive(mNonActive);
  };

  return (
      <div className=" bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
        {/* Page Title */}
        <div className="text-center mb-5">
          <h3 className="text-xl font-bold text-gray-800">
            Payment Collection Report
          </h3>
          <p className="text-gray-500 text-sm">Staff: {user?.name}</p>
        </div>

        {/* Total Collection */}
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-4 rounded-xl shadow-md mb-6 text-white hover:scale-[1.01] transition-transform duration-300">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium">Total Collection</h3>
              <p className="text-2xl font-bold mt-1">
                RM {totalCollection.toFixed(2)}
              </p>
            </div>
            <div className="bg-white/20 p-3 rounded-full">
              <FontAwesomeIcon
                icon={faMoneyBill}
                className="text-white text-xl"
              />
            </div>
          </div>
        </div>

        {/* Overall Report */}
        <div className="bg-white p-4 rounded-xl shadow-md">
          <div className="flex items-center mb-4">
            <FontAwesomeIcon
              icon={faChartPie}
              className="text-green-600 text-lg mr-2"
            />
            <h3 className="text-lg font-bold text-gray-800">Overall Report</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Link to="/payment-actives">
              <div className="bg-green-50 border border-green-200 p-4 rounded-lg hover:shadow-md transition-shadow duration-300">
                <div className="flex justify-between items-center">
                  <div>
                    <h4 className="text-green-700 font-semibold text-base">
                      Active
                    </h4>
                    <p className="text-xl font-bold mt-1 text-gray-800">
                      RM {activeTotal.toFixed(2)}
                    </p>
                  </div>
                  <div className="bg-green-100 p-3 rounded-full">
                    <FontAwesomeIcon
                      icon={faChartLine}
                      className="text-green-600 text-lg"
                    />
                  </div>
                </div>
              </div>
            </Link>

            <Link to="/payment-nonactives">
              <div className="bg-red-50 border border-red-200 p-4 rounded-lg hover:shadow-md transition-shadow duration-300">
                <div className="flex justify-between items-center">
                  <div>
                    <h4 className="text-red-700 font-semibold text-base">
                      Non-Active
                    </h4>
                    <p className="text-xl font-bold mt-1 text-gray-800">
                      RM {nonActiveTotal.toFixed(2)}
                    </p>
                  </div>
                  <div className="bg-red-100 p-3 rounded-full">
                    <FontAwesomeIcon
                      icon={faMoneyBill}
                      className="text-red-600 text-lg"
                    />
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </div>

        {/* Weekly Report */}
        <div className="bg-white p-4 rounded-xl shadow-md mt-5">
          <div className="flex items-center mb-4">
            <FontAwesomeIcon
              icon={faChartLine}
              className="text-blue-600 text-lg mr-2"
            />
            <h3 className="text-lg font-bold text-gray-800">
              Weekly Report (Last 7 Days)
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-green-50 border border-green-200 p-4 rounded-lg hover:shadow-md transition-shadow duration-300">
              <div className="flex justify-between items-center">
                <div>
                  <h4 className="text-green-700 font-semibold text-base">
                    Active
                  </h4>
                  <p className="text-xl font-bold mt-1 text-gray-800">
                    RM {weeklyActive.toFixed(2)}
                  </p>
                </div>
                <div className="bg-green-100 p-3 rounded-full">
                  <FontAwesomeIcon
                    icon={faChartLine}
                    className="text-green-600 text-lg"
                  />
                </div>
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 p-4 rounded-lg hover:shadow-md transition-shadow duration-300">
              <div className="flex justify-between items-center">
                <div>
                  <h4 className="text-red-700 font-semibold text-base">
                    Non-Active
                  </h4>
                  <p className="text-xl font-bold mt-1 text-gray-800">
                    RM {weeklyNonActive.toFixed(2)}
                  </p>
                </div>
                <div className="bg-red-100 p-3 rounded-full">
                  <FontAwesomeIcon
                    icon={faMoneyBill}
                    className="text-red-600 text-lg"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Monthly Report */}
        <div className="bg-white p-4 rounded-xl shadow-md mt-5">
          <div className="flex items-center mb-4">
            <FontAwesomeIcon
              icon={faChartLine}
              className="text-purple-600 text-lg mr-2"
            />
            <h3 className="text-lg font-bold text-gray-800">
              Monthly Report (This Month)
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-green-50 border border-green-200 p-4 rounded-lg hover:shadow-md transition-shadow duration-300">
              <div className="flex justify-between items-center">
                <div>
                  <h4 className="text-green-700 font-semibold text-base">
                    Active
                  </h4>
                  <p className="text-xl font-bold mt-1 text-gray-800">
                    RM {monthlyActive.toFixed(2)}
                  </p>
                </div>
                <div className="bg-green-100 p-3 rounded-full">
                  <FontAwesomeIcon
                    icon={faChartLine}
                    className="text-green-600 text-lg"
                  />
                </div>
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 p-4 rounded-lg hover:shadow-md transition-shadow duration-300">
              <div className="flex justify-between items-center">
                <div>
                  <h4 className="text-red-700 font-semibold text-base">
                    Non-Active
                  </h4>
                  <p className="text-xl font-bold mt-1 text-gray-800">
                    RM {monthlyNonActive.toFixed(2)}
                  </p>
                </div>
                <div className="bg-red-100 p-3 rounded-full">
                  <FontAwesomeIcon
                    icon={faMoneyBill}
                    className="text-red-600 text-lg"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
  );
};

export default SewaPayment;
